import type { PhaseOneDto, PhaseSourceChipDto, WizardStepDto } from '@bk/contracts';
import { useState } from 'react';
import { Figures } from './Fase';
import { PhaseFrame, usePhase } from './fasi-comune';

/**
 * La fase 1 «L'impresa» (docs/mockup/percorso-sinapsi/04-fase1; contratto n. 1 versione 2.0, art. 8, fase 1; ADR 0174):
 * i numeri con la fonte, la classe dimensionale UE con le imprese collegate (calcolata dallo studio), cosa fa e a chi
 * vende con le spille delle fonti, il gruppo, i contratti collettivi probabili e la domanda su quelli applicati.
 */
export function Fase1Page({ next, onNext, sealing, stopped = false }: { next: WizardStepDto | null; onNext: () => void; sealing: boolean; stopped?: boolean }) {
  const { q, reload, confirm, redo } = usePhase<PhaseOneDto>(1);
  return (
    <PhaseFrame
      d={q.data}
      error={q.error}
      eyebrow="Fase 1 · L’impresa"
      title={(d) => `${d.azienda ?? 'La tua azienda'}, al suo meglio`}
      big
      working="Preparo l’impresa"
      questionsIntro="Solo ciò che sai con certezza e che nessuna fonte dà; ciò che ti ho chiesto nella fase 0 non te lo richiedo."
      questionsEmpty="Nessuna domanda in questa fase: i contratti collettivi probabili restano segnati come tali finché non li confermi."
      scrapIntro=""
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
    >
      {(d) => (
        <>
          <Figures list={d.numeri} />
          <SizeClass d={d} />
          <WhatItDoes d={d} />
          {d.gruppo && <Group d={d} />}
          {d.conti && <Accounts d={d} />}
          {d.contratti.length > 0 && <Contracts d={d} />}
        </>
      )}
    </PhaseFrame>
  );
}

function SizeClass({ d }: { d: PhaseOneDto }) {
  const c = d.classe;
  return (
    <section className="sn-panel" style={{ gap: 10 }}>
      <div className="sn-ph">
        <h2 className="sn-h2">La classe dimensionale</h2>
        <span className="lbl">Definizione UE 2003/361 · calcolata dallo studio</span>
      </div>
      <div className="sn-split">
        <dl className="sn-dl">
          <dt>Da sola</dt>
          <dd>
            {c.da_sola ?? 'da confermare'} {c.da_sola_perche} {c.manca && <span className="sn-src gold">· {c.manca} da confermare</span>}
          </dd>
          <dt>Imprese collegate</dt>
          <dd>
            {c.collegate.length
              ? c.collegate.map((x) => (
                  <span key={x.nome} style={{ display: 'block' }}>
                    {x.nome}, {x.legame} <span className="sn-src">· {x.fonte}</span>
                    {x.dati && <span className="sn-src gold"> · {x.dati}</span>}
                  </span>
                ))
              : 'nessuna nell’atto'}
          </dd>
          <dt>Classe che vale</dt>
          <dd style={{ fontWeight: 700 }}>{c.vale ? `${c.vale}${c.collegate.length ? ', per il gruppo di cui fa parte' : ''}` : <span className="sn-gold">da confermare{c.manca ? `: manca ${c.manca}` : ''}</span>}</dd>
        </dl>
        <p className="sn-why" style={{ fontSize: 13 }}>
          <span className="q">Perché?</span> {c.perche}
        </p>
      </div>
    </section>
  );
}

function WhatItDoes({ d }: { d: PhaseOneDto }) {
  const [shown, setShown] = useState<PhaseSourceChipDto | null>(null);
  const v = d.a_chi_vende;
  const who = [v.imprese ? 'imprese' : null, v.privati ? 'privati' : null, v.pubblica_amministrazione ? 'pubblica amministrazione' : null].filter(Boolean);
  return (
    <section className="sn-panel" style={{ gap: 12 }}>
      <div className="sn-ph">
        <h2 className="sn-h2">Cosa fa e a chi vende</h2>
        <span className="lbl">Scritto dal consulente, solo dai fatti del fascicolo</span>
      </div>
      <p className="sn-prose">
        {d.cosa_fa.frasi.map((f, k) => (
          <span key={k}>
            <span className={f.consulente ? 'mine' : ''} title={f.consulente ? 'Secondo il consulente: puoi confermarlo o correggerlo in «Parliamone»' : undefined}>
              {f.testo}
            </span>
            {f.fonti.map((i) => (
              <sup key={i}>{i + 1}</sup>
            ))}{' '}
          </span>
        ))}
        {!d.cosa_fa.frasi.length && <span className="sn-gold">Da confermare: le fonti non dicono abbastanza.</span>}
      </p>
      {who.length > 0 && (
        <p className="sn-note" style={{ fontSize: 14 }}>
          A chi vende: {who.join(', ')}
          {v.dove ? `; ${v.dove}` : ''}.
        </p>
      )}
      <div className="sn-chips">
        {d.cosa_fa.fonti.map((c, k) => (
          <button key={k} type="button" className={`sn-chip ${c.grado ?? ''}`} onClick={() => setShown(shown === c ? null : c)} aria-pressed={shown === c}>
            {k + 1}. {c.testo}
          </button>
        ))}
      </div>
      {shown && (
        <p className="sn-why" style={{ fontSize: 13 }}>
          <span className="q">{shown.testo}.</span> {shown.citazione ? `«${shown.citazione}»` : 'Il dato come lo scrive la fonte.'}{' '}
          {shown.url && (
            <a href={shown.url} target="_blank" rel="noreferrer">
              {shown.url.replace(/^https?:\/\//, '')}
            </a>
          )}
        </p>
      )}
      <p className="sn-note">
        <span className="sn-cyan">Perché?</span> {d.cosa_fa.perche}
      </p>
    </section>
  );
}

function Group({ d }: { d: PhaseOneDto }) {
  const g = d.gruppo!;
  return (
    <section className="sn-panel" style={{ gap: 10 }}>
      <h2 className="sn-h2">Il gruppo</h2>
      <div className="sn-group">
        {g.capogruppo && (
          <>
            <div className="who">
              <span className="sn-dot violet" style={{ width: 14, height: 14, boxShadow: '0 0 14px #8a5cff' }} />
              <b>{g.capogruppo.nome}</b>
              {g.capogruppo.codice_fiscale && <span className="sn-src mute">{g.capogruppo.codice_fiscale}</span>}
            </div>
            <span className="wire" />
          </>
        )}
        <div className="who">
          <span className="sn-dot on" style={{ width: 14, height: 14 }} />
          <b>{d.azienda ?? 'La tua azienda'}</b>
          <span className="sn-src mute">
            {[g.quota, g.dal ? `dal ${g.dal}` : null].filter(Boolean).join(' ')}
          </span>
        </div>
      </div>
      <p className="sn-note">{g.nota}</p>
    </section>
  );
}

function Contracts({ d }: { d: PhaseOneDto }) {
  return (
    <section className="sn-panel" style={{ gap: 10 }}>
      <div className="sn-ph">
        <h2 className="sn-h2">I contratti collettivi</h2>
        <span className="lbl">{d.contratti.some((k) => k.grado === 'T') ? 'Detti da te · T' : d.contratti.some((k) => k.grado === 'scelta_timonio') ? 'Scelta di Timonio' : 'Probabili dal fascicolo di settore'}</span>
      </div>
      <div className="sn-scrap">
        {d.contratti.map((k) => (
          <span key={`${k.codice}-${k.nome}`} style={{ display: 'contents' }}>
            <span>
              <b>{k.nome}</b>
              {k.codice && <span className="sn-src mute"> · {k.codice}</span>}
              {k.a_chi && <span className="sn-note"> · {k.a_chi}</span>}
              {k.perche && <span className="sn-note" style={{ display: 'block' }}>{k.perche}</span>}
            </span>
            <span className={`o ${k.grado === 'T' ? 'ok' : ''}`}>{k.grado === 'T' ? 'Applicato · T' : k.grado === 'scelta_timonio' ? 'Scelta di Timonio' : 'Probabile'}</span>
          </span>
        ))}
      </div>
    </section>
  );
}

/** I conti letti (art. 3.56): il gradino, gli indici con la formula, i fatti della nota integrativa, il settore, cosa dicono i numeri. */
function Accounts({ d }: { d: PhaseOneDto }) {
  const c = d.conti!;
  return (
    <section className="sn-panel" style={{ gap: 12 }}>
      <h2 className="sn-h2">I conti letti</h2>
      <div className={`sn-step3 ${c.gradino === 1 ? 'ok' : ''}`}>
        <span>{c.gradino}</span>
        <span>
          <b style={{ color: '#eaf0ff' }}>{c.gradino_testo.split(':')[0]}:</b>
          {c.gradino_testo.slice(c.gradino_testo.indexOf(':') + 1)}
        </span>
      </div>
      <p className="sn-ey">Gli indici · calcolati dallo studio</p>
      <div>
        <div className="sn-grid4 h">
          <span>Indice</span>
          <span>Formula, uguale per ogni azienda</span>
          <span>Esercizi · trend</span>
          <span>Valore</span>
        </div>
        {c.indici.map((x) => (
          <div key={x.nome} className="sn-grid4">
            <span style={{ fontWeight: 600 }}>{x.nome}</span>
            <span className="sn-soft">{x.formula}</span>
            <span className="m">{x.esercizi === x.trend ? x.esercizi : `${x.esercizi} · ${x.trend}`}</span>
            <span className={x.da_confermare ? 'sn-src gold' : ''} title={x.nota ?? undefined}>
              {x.valore}
            </span>
          </div>
        ))}
      </div>
      <p className="sn-note">Ogni indice porta la voce di bilancio e l’esercizio di riferimento; due esercizi diversi non si mettono insieme senza dirlo.</p>
      <p className="sn-ey" style={{ marginTop: 6 }}>
        I fatti della nota integrativa e della relazione
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {c.fatti.map((f, k) => (
          <span key={k} className={`sn-dash ${f.valore ? 'ok' : ''}`} title={f.citazione ? `«${f.citazione}»` : undefined}>
            {f.valore ? `${f.valore} · ${f.esercizio}` : <>{f.nome} · <span className="sn-gold">da confermare</span></>}
          </span>
        ))}
      </div>
      {c.settore.length > 0 && (
        <>
          <p className="sn-ey" style={{ marginTop: 6 }}>
            Il confronto con il settore
          </p>
          <div>
            <div className="sn-grid3 h">
              <span>Rapporto</span>
              <span>{d.azienda ?? 'L’azienda'}</span>
              <span>Settore</span>
            </div>
            {c.settore.map((x) => (
              <div key={x.rapporto} className="sn-grid3">
                <span style={{ fontWeight: 600 }}>{x.rapporto}</span>
                <span className={x.azienda === 'da confermare' ? 'sn-src gold' : 'sn-mono'}>{x.azienda}</span>
                <span>
                  {x.settore} {x.fonte && <span className="m">· {x.fonte}</span>}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="sn-said">
        <p className="sn-ey cyan">Cosa dicono i numeri · scritto dal consulente</p>
        {c.numeri.map((n, k) => (
          <p key={k} style={{ fontSize: 15, color: n.fonte ? '#eaf0ff' : '#c3cbe0' }}>
            {n.testo} {n.fonte && <span className="sn-src">{n.fonte}</span>}
          </p>
        ))}
        {!c.numeri.length && <p className="sn-soft">Il fascicolo non ha ancora numeri da leggere: non scrivo nulla finché non arrivano.</p>}
        <p className="sn-note">Ogni frase porta la voce, l’esercizio e la fonte; nessuna frase senza un numero del fascicolo. Nessun giudizio: lo scarto è della fase 10, le proposte della fase 11.</p>
      </div>
    </section>
  );
}
